import { useState } from 'react'
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Checkbox, Typography, Box, TextField, InputAdornment,
  TablePagination, Chip, Tooltip
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { DCEligibleItem } from '../../types/dc.types'

interface EligibleItemsTableProps {
  items: DCEligibleItem[]
  total: number
  page: number
  limit: number
  search: string
  onSearchChange: (search: string) => void
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  selectedIds: number[]
  onSelectionChange: (ids: number[]) => void
}

function formatRs(val: number | null | undefined): string {
  if (val == null || val === 0) return '-'
  return `Rs.${val.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
}

function formatRsDec(val: number | null | undefined): string {
  if (val == null || val === 0) return '-'
  return `Rs.${val.toFixed(2)}`
}

export default function EligibleItemsTable({
  items, total, page, limit, search,
  onSearchChange, onPageChange, onLimitChange,
  selectedIds, onSelectionChange
}: EligibleItemsTableProps) {
  const [localSearch, setLocalSearch] = useState(search)

  const handleSearchSubmit = () => {
    onSearchChange(localSearch)
  }

  const handleToggle = (medicineId: number) => {
    if (selectedIds.includes(medicineId)) {
      onSelectionChange(selectedIds.filter(id => id !== medicineId))
    } else {
      onSelectionChange([...selectedIds, medicineId])
    }
  }

  const handleToggleAll = () => {
    const currentPageIds = items.map(i => i.medicine_id)
    const allSelected = currentPageIds.every(id => selectedIds.includes(id))
    if (allSelected) {
      onSelectionChange(selectedIds.filter(id => !currentPageIds.includes(id)))
    } else {
      const newIds = [...new Set([...selectedIds, ...currentPageIds])]
      onSelectionChange(newIds)
    }
  }

  const allOnPageSelected = items.length > 0 && items.every(i => selectedIds.includes(i.medicine_id))

  // Compute page totals
  const pageTotalSavings = items.reduce((sum, i) => sum + (i.monthly_savings || 0), 0)

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search medicines..."
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
          sx={{ width: 300 }}
        />
        {selectedIds.length > 0 && (
          <Chip
            label={`${selectedIds.length} selected`}
            color="primary"
            onDelete={() => onSelectionChange([])}
          />
        )}
        <Box sx={{ ml: 'auto' }}>
          <Typography variant="body2" color="text.secondary">
            Page savings: <strong>{formatRs(pageTotalSavings)}/mo</strong>
          </Typography>
        </Box>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 600, whiteSpace: 'nowrap' } }}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allOnPageSelected}
                  indeterminate={selectedIds.length > 0 && !allOnPageSelected}
                  onChange={handleToggleAll}
                />
              </TableCell>
              <TableCell>Medicine</TableCell>
              <TableCell>Pack</TableCell>
              <TableCell>Distributor</TableCell>
              <TableCell>Offer</TableCell>
              <TableCell align="right">Stores</TableCell>
              <TableCell align="right">Packs/mo</TableCell>
              <TableCell align="right">Local/pk</TableCell>
              <TableCell align="right">DC/pk</TableCell>
              <TableCell align="right">Save/mo</TableCell>
              <TableCell align="right">Save%</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    No eligible items found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => {
                const savingsPct = item.local_cost_per_pack && item.local_cost_per_pack > 0 && item.monthly_savings != null
                  ? (item.monthly_savings / ((item.monthly_packs || 0) * item.local_cost_per_pack)) * 100
                  : null
                const isNegative = (item.monthly_savings || 0) < 0

                return (
                  <TableRow
                    key={item.medicine_id}
                    hover
                    selected={selectedIds.includes(item.medicine_id)}
                    onClick={() => handleToggle(item.medicine_id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox checked={selectedIds.includes(item.medicine_id)} />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={item.manufacturer_name || ''} placement="top">
                        <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 200 }}>
                          {item.medicine_name}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 80 }}>
                        {item.pack_size}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 130 }}>
                        {item.best_distributor_name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {item.best_offer_text || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={item.stores_qualifying}
                        size="small"
                        color={item.stores_qualifying === 3 ? 'success' : item.stores_qualifying === 2 ? 'info' : 'default'}
                        variant="outlined"
                        sx={{ minWidth: 28 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {item.monthly_packs != null ? item.monthly_packs.toFixed(0) : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatRsDec(item.local_cost_per_pack)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatRsDec(item.dc_cost_per_pack)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color={isNegative ? 'error.main' : 'success.main'}
                      >
                        {item.monthly_savings != null ? formatRs(item.monthly_savings) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        color={isNegative ? 'error.main' : savingsPct && savingsPct > 10 ? 'success.main' : 'text.secondary'}
                      >
                        {savingsPct != null ? `${savingsPct.toFixed(1)}%` : '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page - 1}
        rowsPerPage={limit}
        onPageChange={(_, p) => onPageChange(p + 1)}
        onRowsPerPageChange={(e) => onLimitChange(parseInt(e.target.value))}
        rowsPerPageOptions={[25, 50, 100]}
      />
    </Box>
  )
}
